import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  Pause as ParkIcon,
  PlayArrow as PursueIcon,
  NoteAdd as NoteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { Lead, NoteDialog, PromoteDialog, StatusDialog } from './LeadDetail';

/**
 * Compact, in-place triage card for a lead. Rendered inside an expanded row
 * in the rule's Results tab so the lawyer can read provider context, review
 * fire data, add notes, and disposition the lead without leaving the rule.
 *
 * Reuses the same dialog components LeadDetail uses for action UX parity.
 */
const LeadInline = ({ leadId }: { leadId: string }) => {
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [noteOpen, setNoteOpen] = useState(false);
  const [statusDialog, setStatusDialog] = useState<{ open: boolean; target: string }>({ open: false, target: '' });
  const [promoteOpen, setPromoteOpen] = useState(false);

  const fetchLead = async () => {
    setError(null);
    try {
      const res = await api.get(`/explore/leads/${leadId}/`);
      setLead(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLead(null);
    fetchLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  if (loading) {
    return <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>;
  }
  if (error || !lead) {
    return <Typography variant="caption" color="error" sx={{ py: 1, display: 'block' }}>{error || 'Lead not found'}</Typography>;
  }

  const latestFire = lead.fires?.[0];
  const provider = latestFire?.analysis_json || {};

  return (
    <Box sx={{ py: 1.5 }}>
      <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper' }}>
        {/* Existing case warning */}
        {lead.case_id && (
          <Box sx={{ mb: 1.5, p: 1, backgroundColor: 'rgba(76,175,80,0.08)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Already on case:{' '}
              <Box
                component="span"
                onClick={() => navigate(`/cases/${lead.case_id}`)}
                sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
              >
                Open case ↗
              </Box>
            </Typography>
          </Box>
        )}
        {!lead.case_id && lead.existing_cases_for_subject?.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1, backgroundColor: 'rgba(255,152,0,0.08)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Subject is on{' '}
              {lead.existing_cases_for_subject.map((c, i) => (
                <Box component="span" key={c.case_id}>
                  <Box
                    component="span"
                    onClick={() => navigate(`/cases/${c.case_id}`)}
                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    {c.case_name}
                  </Box>
                  {i < lead.existing_cases_for_subject.length - 1 ? ', ' : ''}
                </Box>
              ))}
            </Typography>
          </Box>
        )}

        {/* Two-column grid: provider context (left) + latest-fire metrics (right) */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {(provider.display_name || provider.primary_taxonomy_code || provider.practice_state) && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Provider
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 0.25, columnGap: 1 }}>
                {provider.entity_type && <>
                  <Typography variant="caption" color="text.secondary">Entity</Typography>
                  <Typography variant="caption">{provider.entity_type}</Typography>
                </>}
                {provider.primary_taxonomy_code && <>
                  <Typography variant="caption" color="text.secondary">Taxonomy</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{provider.primary_taxonomy_code}</Typography>
                </>}
                {(provider.practice_city || provider.practice_state) && <>
                  <Typography variant="caption" color="text.secondary">Practice</Typography>
                  <Typography variant="caption">{[provider.practice_city, provider.practice_state].filter(Boolean).join(', ')}</Typography>
                </>}
                {provider.npi_is_active !== undefined && <>
                  <Typography variant="caption" color="text.secondary">NPI active</Typography>
                  <Typography variant="caption">{provider.npi_is_active ? 'yes' : 'no'}</Typography>
                </>}
              </Box>
            </Box>
          )}

          {latestFire && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Latest fire · v{latestFire.rule_version}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 0.25, columnGap: 1 }}>
                {latestFire.metric_value && <>
                  <Typography variant="caption" color="text.secondary">Metric</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{latestFire.metric_value}</Typography>
                </>}
                <Typography variant="caption" color="text.secondary">Amount</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  ${parseFloat(latestFire.amount_usd || '0').toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
                {Object.entries(latestFire.analysis_json || {})
                  .filter(([k]) => !['display_name', 'entity_type', 'primary_taxonomy_code', 'practice_city', 'practice_state', 'npi_is_active'].includes(k))
                  .slice(0, 4)
                  .map(([k, v]) => (
                    <Box component="span" key={k} sx={{ display: 'contents' }}>
                      <Typography variant="caption" color="text.secondary">{k}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{String(v)}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Notes */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Notes
          </Typography>
          {lead.notes ? (
            <Box sx={{ p: 1, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 1, whiteSpace: 'pre-wrap' }}>
              <Typography variant="caption">{lead.notes}</Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">No notes yet.</Typography>
          )}
          {lead.disposition_reason && (
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Disposition: {lead.disposition_reason}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Fire count if there's more than one */}
        {lead.fires?.length > 1 && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={`${lead.fires.length} fires total`}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button startIcon={<NoteIcon />} onClick={() => setNoteOpen(true)} size="small" sx={{ textTransform: 'none' }}>
            Note
          </Button>
          <Button
            startIcon={<ParkIcon />}
            onClick={() => setStatusDialog({ open: true, target: 'closed' })}
            disabled={lead.status === 'closed'}
            size="small" sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
          {lead.case_id ? (
            <Button
              variant="contained" color="success"
              size="small"
              onClick={() => navigate(`/cases/${lead.case_id}`)}
              sx={{ textTransform: 'none' }}
            >
              Open Case
            </Button>
          ) : (
            <Button
              variant="contained" color="success"
              startIcon={<PursueIcon />}
              onClick={() => setPromoteOpen(true)}
              size="small" sx={{ textTransform: 'none' }}
            >
              Promote to Case
            </Button>
          )}
        </Box>
      </Paper>

      {/* Dialogs (shared with LeadDetail) */}
      <NoteDialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        leadId={lead.lead_id}
        onSaved={fetchLead}
      />
      <StatusDialog
        open={statusDialog.open}
        targetStatus={statusDialog.target}
        onClose={() => setStatusDialog({ open: false, target: '' })}
        leadId={lead.lead_id}
        onSaved={fetchLead}
      />
      <PromoteDialog
        open={promoteOpen}
        onClose={() => setPromoteOpen(false)}
        lead={lead}
        onPromoted={(caseId: string) => { setPromoteOpen(false); navigate(`/cases/${caseId}`); }}
      />
    </Box>
  );
};

export default LeadInline;
